/**
 * Gemeinsame Beschriftung der Telematik-Rohfelder — für die Telemetrie der Streckenseite
 * („Heute gefahren") und die Box „Zuletzt gemeldet" in der Fahrzeugakte. Eine Quelle, damit
 * beide dieselben Namen und Einheiten zeigen und ein neues Protokollfeld nur hier ergänzt
 * werden muss.
 *
 * Grundsatz (Geräteprotokoll, docs/geraeteprotokoll.md im Geräte-Repo als verbindliche
 * Quelle): anzeigen, was das Gerät wirklich sendet; Unbekanntes hinten anhängen, nichts
 * verschweigen. Eine Einheit steht nur dort, wo sie sicher ist — lieber keine als eine
 * falsche. So trägt die Anzeige jedes Gerät und jeden Protokollstand, nicht nur einen.
 */
export const FELD: Record<string, { label: string; einheit?: string }> = {
  // Kernwerte des Protokolls
  geschwindigkeit: { label: 'Geschwindigkeit', einheit: 'km/h' },
  kmStand: { label: 'Kilometerstand', einheit: 'km' },
  betriebsstunden: { label: 'Betriebsstunden', einheit: 'h' },
  fuellstand: { label: 'Kraftstoff', einheit: '%' },
  ladestand: { label: 'Ladung', einheit: '%' },
  batterieSoc: { label: 'Batterie-Ladung', einheit: '%' },
  antriebsart: { label: 'Antriebsart' },
  zuendungAn: { label: 'Zündung' },
  richtung: { label: 'Richtung', einheit: '°' },
  fehlertext: { label: 'Fehler' },
  // Zusatzfelder, die FMS-Geräte über die Kernwerte hinaus senden (Einheit nur, wo sicher)
  aussentemperatur: { label: 'Außentemperatur', einheit: '°C' },
  motorlast: { label: 'Motorlast', einheit: '%' },
  fahrpedal: { label: 'Fahrpedal', einheit: '%' },
  tachoGeschwindigkeit: { label: 'Tacho-Geschwindigkeit', einheit: 'km/h' },
  momentanverbrauch: { label: 'Momentanverbrauch' },
  verbrauch: { label: 'Verbrauch' },
  kraftstoffGesamt: { label: 'Kraftstoff gesamt' },
  fahrzeugBewegung: { label: 'In Bewegung' },
  bremse: { label: 'Bremse' },
  tempomatAktiv: { label: 'Tempomat' },
  fahrer1Zustand: { label: 'Fahrer 1' },
  fahrer2Zustand: { label: 'Fahrer 2' },
  fahrgestellnummer: { label: 'Fahrgestellnummer (VIN)' },
  geraetesoftware: { label: 'Gerätesoftware' },
  fmsSoftwareVersion: { label: 'FMS-Softwareversion' },
  // Metadaten, die manche Meldungen tragen
  gemessenAm: { label: 'zuletzt gemessen' },
  protokoll: { label: 'Protokoll' },
}

/** Reihenfolge der bekannten Felder; Unbekanntes wird dahinter alphabetisch angehängt. */
const FELD_REIHE = Object.keys(FELD)

/** Ein Telemetriewert menschlich — Wahrheitswerte als ja/nein, der Rest wie gesendet. */
export function wertText(wert: unknown): string {
  if (wert === null || wert === undefined) return '—'
  if (typeof wert === 'boolean') return wert ? 'ja' : 'nein'
  return String(wert)
}

/**
 * Die anzuzeigenden Feldnamen eines rohen Zustands, bekannte zuerst (in {@link FELD}-Reihenfolge),
 * Unbekanntes alphabetisch dahinter. `ausschluss` nimmt heraus, was anderswo steht — die
 * Position auf der Karte und doppelte Metadaten.
 */
export function telemetriefelder(
  zustand: Record<string, unknown>,
  ausschluss: Set<string>,
): string[] {
  const vorhanden = Object.keys(zustand).filter((k) => !ausschluss.has(k))
  const bekannt = FELD_REIHE.filter((k) => vorhanden.includes(k))
  const rest = vorhanden.filter((k) => !FELD_REIHE.includes(k)).sort()
  return [...bekannt, ...rest]
}
